import React from 'react';
import { formatDate, properDate } from 'lib/dateUtilities';
import { isToday } from 'date-fns';
import selectors from 'selectors/selectors';
import setQuery from 'lib/setQuery.js';
import { slugify } from 'lib/stringUtilities';
import { patchTodo } from 'redux/actions/leadsActions.js';
import { useSelector } from 'react-redux';
import SVG from 'images/svg/SVG.js';
import todoStatusIconMap from 'lib/constants/todoStatusIconMap.js';
import formatDatetimeTheUniverseLifeAndEverything from 'lib/formatDatetimeTheUniverseLifeAndEverything.js';

const getDueMeta = (item, displayFull) => {
  const date = item.target_datetime;
  let due = formatDate(date, 'h:mm aaa');
  if (displayFull === true && !isToday(new Date(date))) {
    due = `${formatDate(date, 'MM/dd/yyyy')} @ ${due}`;
  }
  if (date.toString().includes(':42')) {
    due = formatDatetimeTheUniverseLifeAndEverything(date);
  }
  return {
    label: 'Due Date',
    value: due,
    className: item.overdue ? 'overdue' : '',
    icon: 'TodoSchedule',
  };
};

const getUserMeta = (item) => {
  if (!item.assignee_user) return false;
  const value = `${item.assignee_user.first_name} ${item.assignee_user.last_name}`;
  return {
    label: 'Assigned to',
    value: value,
    icon: 'User',
  };
};

const getServicePersonMeta = (item, servicePerson) => {
  if (!servicePerson) {
    return false;
  }
  const value = `${servicePerson.firstName} ${servicePerson.lastName}`.trim();
  return {
    label: 'Service Person',
    value: value,
    icon: 'Person',
  };
};

const TodoItem = React.memo(({ todoId, displayFull, view }) => {
  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const item = useSelector(selectors.todo(todoId));
  const todoTypes = useSelector((state) => state.app.todo_types);
  const todoStatuses = useSelector(selectors.todoStatuses);
  const servicePersons = useSelector(selectors.serviceProviderServicePersons);
  const modal = useSelector(selectors.modal);
  const isMobile = useSelector(selectors.mobile);

  if (!item) {
    return null;
  }

  const type = (todoTypes.find((n) => n.id === item.todo_type) || {}).type;
  const status = (todoStatuses.find((n) => n.id === item.todo_status) || {}).status;
  const statusSlug = `${slugify(status)}`;
  const typeSlug = `${slugify(type)}`;
  const lead = item.lead || {};
  const pipelineLeadId = item.pipeline_lead_id;
  const pipelineLead = ((item.lead || {}).pipeline_leads || []).find((n) => n.id === pipelineLeadId) || {};
  const servicePersonId = pipelineLead.service_person_id;
  const servicePerson = servicePersons.find((n) => n.id === servicePersonId);
  const metaItems = [getDueMeta(item, displayFull), getUserMeta(item)].filter((n) => !!n);
  const description = `${String(item.todo_description || '') //
    .slice(0, 60)}${String(item.todo_description || '').length > 60 ? '...' : ''}`;
  const leadName = lead.first_name ? `${lead.first_name} ${lead.last_name}` : '';

  const openLeadModal = (e, edit = false) => {
    e.stopPropagation();
    edit = !!modal && isMobile ? true : edit;
    if (view === 'notificationSlider') {
      setQuery({
        lead: item.lead_id,
        modal: 'true',
        eid: null,
      });
    }
    if (!!modal && !isMobile && !edit) {
      return;
    }
    setQuery({
      lead: item.lead_id,
      todoId: edit ? item.id : null,
      tab: edit ? 'todos' : undefined,
      modal: 'true',
    });
  };

  const handleMarkAsClick = (e) => {
    e.stopPropagation();
    const newStatus = status === 'Active' ? 'Completed' : status === 'Completed' ? 'Active' : undefined;
    const newStatusId = (todoStatuses.find((n) => n.status === newStatus) || {}).id;
    if (newStatusId) {
      patchTodo({
        id: item.id,
        statusId: newStatusId,
        serviceProviderId,
      });
    }
  };

  return (
    <div onClick={(e) => openLeadModal(e)} className={`todo-item lead-data-card ${statusSlug} ${displayFull ? 'full' : ''}`}>
      <div className={'todo-item-header-wrapper'}>
        <div className={'left'}>
          <span className={`todo-type-icon ${typeSlug}`}>
            <SVG icon={todoStatusIconMap[typeSlug]} basic={true} />
          </span>
          <div className={'todo-item-header'}>
            <div className={`todo-item-name`}>{leadName}</div>
            <div className={`todo-type-label`}>{`${type}`}</div>
          </div>
        </div>
        <div className={'card-controls'}>
          <div className={'card-control'} onClick={(e) => openLeadModal(e, true)} data-title={'Edit'}>
            <SVG icon={'Notes'} />
          </div>
          <div className={`card-control ${status === 'Completed' ? 'completed' : ''}`} onClick={(e) => handleMarkAsClick(e)} data-title={statusSlug === 'completed' ? 'Mark as Active' : 'Mark as Complete'}>
            <SVG icon={'TodoComplete'} />
          </div>
        </div>
      </div>
      {description && <div className={`todo-item-description`}>{description}</div>}
      <div className={`card-meta`}>
        {metaItems.map((n, i) => (
          <div key={i} className={`card-meta-item ${n.label.toLowerCase()} ${n.className || ''}`} data-title={n.label}>
            <SVG icon={n.icon} />
            <span className="value">{n.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default TodoItem;
